import {
	Alert,
	Button,
	Checkbox,
	DatePicker,
	Empty,
	Flex,
	Space,
	Spin,
	Tag,
	Typography,
	message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';

import {
	FetchRunStatus,
	useFetchCertRangeMutation,
	useGetCertFetchRunsQuery,
	useGetCertMessageWarningsQuery,
} from '../../redux/api/material-certs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const STATUS_COLOR: Record<FetchRunStatus, string> = {
	queued: 'default',
	running: 'blue',
	succeeded: 'green',
	failed: 'red',
};

/**
 * Sweep a date range out of the Alro mailbox.
 *
 * Two things happen on this page: the schedule picks up new mail on its own,
 * and this is how the archive gets worked through -- a range at a time, so the
 * results can be judged before reaching further back.
 *
 * Re-running a range costs nothing. Every message examined is remembered, so a
 * second pass over the same weeks skips what it already knows.
 */
export default function FetchPanel() {
	const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
	const [retryFailed, setRetryFailed] = useState(false);

	const [fetchRange, { isLoading: starting }] = useFetchCertRangeMutation();
	const { data: runs = [], isLoading: loadingRuns } = useGetCertFetchRunsQuery(
		undefined,
		{
			// A sweep runs on a worker, so the only way to see it finish is to ask.
			pollingInterval: 5000,
		}
	);
	const { data: warnings = [] } = useGetCertMessageWarningsQuery();

	const run = async () => {
		if (!range) return;
		try {
			await fetchRange({
				after: range[0].format('YYYY-MM-DD'),
				before: range[1].format('YYYY-MM-DD'),
				retry_failed: retryFailed,
			}).unwrap();
			message.success('Fetch queued. Counts appear below as it works.');
		} catch (error) {
			message.error(
				(error as { data?: { detail?: string } })?.data?.detail ||
					'Could not start the fetch.'
			);
		}
	};

	return (
		<Flex vertical gap={16}>
			<Alert
				type="info"
				showIcon
				message="Reading only"
				description="Nothing is marked read, moved or deleted in the mailbox, and nothing attaches itself in ERP. Everything found lands in the review queue."
			/>

			<Space direction="vertical" style={{ width: '100%' }} size={8}>
				<Text strong>Date range</Text>
				<RangePicker
					style={{ width: '100%' }}
					value={range}
					onChange={(value) =>
						setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)
					}
					// Mail cannot arrive from the future, and a range that ends today
					// overlaps the scheduled window harmlessly.
					disabledDate={(current) => current && current > dayjs().endOf('day')}
				/>
				<Checkbox
					checked={retryFailed}
					onChange={(event) => setRetryFailed(event.target.checked)}
				>
					Re-examine messages that failed before
				</Checkbox>
				<Button type="primary" onClick={run} loading={starting} disabled={!range}>
					Fetch this range
				</Button>
				<Text type="secondary" style={{ fontSize: 12 }}>
					A month at a time is a good size. Check the results before reaching
					further back.
				</Text>
			</Space>

			<div>
				<Text strong>Recent fetches</Text>
				{loadingRuns && !runs.length ? (
					<Flex justify="center" style={{ padding: 16 }}>
						<Spin />
					</Flex>
				) : !runs.length ? (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description="No range has been fetched yet."
					/>
				) : (
					runs.map((row) => (
						<div
							key={row.id}
							style={{
								padding: '8px 0',
								borderBottom: '1px solid #f0f0f0',
							}}
						>
							<Flex justify="space-between" align="center" gap={8}>
								<Text>
									{row.after} to {row.before}
								</Text>
								<Tag color={STATUS_COLOR[row.status]}>{row.status}</Tag>
							</Flex>
							<Text type="secondary" style={{ fontSize: 12 }}>
								{row.examined_count} examined, {row.ingested_count} new,{' '}
								{row.skipped_count} already known
								{row.failed_count > 0 && `, ${row.failed_count} failed`}
							</Text>
							{row.detail && (
								<div>
									<Text type="danger" style={{ fontSize: 12 }}>
										{row.detail}
									</Text>
								</div>
							)}
						</div>
					))
				)}
			</div>

			{warnings.length > 0 && (
				<div>
					<Text strong>Mail that needs a look</Text>
					<div>
						<Text type="secondary" style={{ fontSize: 12 }}>
							Read but not fully parsed. Certificates for these slips show as
							&quot;No slip&quot; until the slip is fixed or re-read.
						</Text>
					</div>
					{warnings.map((row) => (
						<div
							key={row.id}
							style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}
						>
							<Flex justify="space-between" align="center" gap={8}>
								<Text ellipsis style={{ maxWidth: 320 }}>
									{row.subject || '(no subject)'}
								</Text>
								<Tag color={row.status === 'failed' ? 'red' : 'orange'}>
									{row.status === 'failed' ? 'failed' : 'parse warning'}
								</Tag>
							</Flex>
							<Text type="secondary" style={{ fontSize: 12 }}>
								{row.received_at ? dayjs(row.received_at).format('MMM D, YYYY') : ''}
								{row.status_detail && ` - ${row.status_detail}`}
							</Text>
						</div>
					))}
				</div>
			)}
		</Flex>
	);
}
